import { z } from 'zod'

import type { Timestamp } from 'firebase/firestore'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'

import { Bytes } from 'firebase/firestore'

export const vector2 = z.array(z.number()).length(2)
export const vector3 = z.array(z.number()).length(3)

const canTransform = (coercer: any) => {
  return (val: any) => {
    try {
      z
      coercer(val)
      return true
    } catch (e) {
      return false
    }
  }
}

export class ModelUtils {
  public bufferCoercer
  public bytesForFirestore
  public bytesForAdminFirestore
  public base64StringCoercer
  public base64String
  public booleanCoercer
  public dateCoercer
  public date
  public dateForFirestore

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.bufferCoercer = (s: string | Buffer | Bytes) => {
      if (s instanceof Bytes) return Buffer.from(s.toUint8Array())
      else if (typeof s === 'string') return Buffer.from(s, 'base64')
      else if (s instanceof Buffer) return s
      else throw new Error('bufferCoercer: unexpected type')
    }

    this.bytesForFirestore = z
      .any()
      .refine(canTransform(this.bufferCoercer), (val) => ({
        message: `binary data value couldn't be transformed for saving to firestore`,
      }))
      .transform(this.bufferCoercer)

    this.bytesForAdminFirestore = z
      .any()
      .refine(canTransform(this.bufferCoercer), (val) => ({
        message: `binary data value couldn't be transformed for saving to firestore`,
      }))
      .transform(this.bufferCoercer)

    this.base64StringCoercer = (s: string | Bytes | Buffer | undefined) => {
      if (s === null || s === undefined) return undefined
      else if (typeof s === 'string') return s
      else if (s instanceof Bytes) return s.toBase64()
      else if (s instanceof Buffer) return s.toString('base64')
      else return s.toString('base64')
    }

    this.base64String = z.any().transform(this.base64StringCoercer)

    this.booleanCoercer = (b: string | boolean) => {
      if (typeof b == 'string') {
        if (b == 'true') return true
        else if (b == 'false') return false
        else throw new Error('booleanCoercer: unexpected string value')
      } else if (typeof b == 'boolean') {
        return b
      } else throw new Error('booleanCoercer: unexpected type')
    }

    this.dateCoercer = (
      d: Date | Timestamp | AdminTimestamp | string,
    ): Date => {
      if (d instanceof Date) return d
      else if (typeof d === 'string') return new Date(d)
      else return d.toDate()
    }

    this.date = z
      .union([z.date(), z.string(), z.any()])
      .refine(canTransform(this.dateCoercer), (val) => ({
        message: `date value:  ${val} couldn't be converted to a firestore timestamp`,
      }))
      .transform(this.dateCoercer)

    this.dateForFirestore = z
      .union([z.date(), z.undefined(), z.string()])
      .transform((d) => {
        if (d instanceof Date) {
          return timestamp.fromDate(d)
        } else if (typeof d === 'string') {
          return timestamp.fromDate(new Date(d))
        } else {
          return undefined
        }
      })
  }
}
